'use client'

import { createContext, type PropsWithChildren, useCallback, useContext, useMemo, useRef } from 'react'

export type HeaderSearchOpener = () => void

interface HeaderSearchContextValue {
    isAvailable: boolean
    /** No-op until the header search has mounted and registered its opener. */
    openSearch: () => void
    registerOpener: (opener: HeaderSearchOpener | null) => void
}

const UNAVAILABLE: HeaderSearchContextValue = {
    isAvailable: false,
    openSearch: () => undefined,
    registerOpener: () => undefined,
}

const HeaderSearchContext = createContext<HeaderSearchContextValue>(UNAVAILABLE)

/**
 * Lets controls elsewhere on the page open the site header search.
 * `isAvailable` comes from the route config so the first render already knows
 * whether a header search exists.
 */
export function HeaderSearchProvider({ isAvailable, children }: PropsWithChildren<{ isAvailable: boolean }>) {
    const openerRef = useRef<HeaderSearchOpener | null>(null)
    const registerOpener = useCallback((opener: HeaderSearchOpener | null) => {
        openerRef.current = opener
    }, [])
    const openSearch = useCallback(() => openerRef.current?.(), [])
    const value = useMemo(
        () => ({ isAvailable, openSearch, registerOpener }),
        [isAvailable, openSearch, registerOpener],
    )

    return <HeaderSearchContext.Provider value={value}>{children}</HeaderSearchContext.Provider>
}

export function useHeaderSearch() {
    return useContext(HeaderSearchContext)
}
